import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { tickTimerState, extraTimeChargeView } from '@/lib/serviceTimer';
import { isNetworkFailure } from '@/lib/network/apiErrors';
import { getNetworkState, onReconnect } from '@/lib/network/connectivity';

const POLL_MS = 15000;

export default function useServiceStatus(bookingId, enabled = true) {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkOffline, setNetworkOffline] = useState(false);
  const [timer, setTimer] = useState(null);
  const [extraView, setExtraView] = useState(null);
  const anchorRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!bookingId || !enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      if (!getNetworkState().isOnline) {
        if (!cancelled) {
          setNetworkOffline(true);
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await api.get(`/bookings/${bookingId}/service-status`);
        if (!cancelled) {
          setService(data.service);
          setError(null);
          setNetworkOffline(false);
          anchorRef.current = {
            receivedAt: Date.now(),
            serverMs: Date.parse(data.service.server_now),
          };
          setTimer(tickTimerState(data.service, 0));
          setExtraView(extraTimeChargeView(data.service, 0));
        }
      } catch (e) {
        if (!cancelled) {
          if (isNetworkFailure(e)) {
            setNetworkOffline(true);
            setError(null);
          } else {
            setError(e.response?.data?.detail || 'Could not load service status');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    pollRef.current = setInterval(load, POLL_MS);
    const unsubReconnect = onReconnect(() => {
      if (!cancelled) load();
    });

    return () => {
      cancelled = true;
      unsubReconnect();
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [bookingId, enabled]);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (!service || !anchorRef.current) return undefined;

    tickRef.current = setInterval(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const offsetMs = Date.now() - anchor.receivedAt;
      setTimer(tickTimerState(service, offsetMs));
      setExtraView(extraTimeChargeView(service, offsetMs));
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [service]);

  return { service, loading, error, timer, extraView, networkOffline };
}
